import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Box } from '@mui/material';
import Image from '../components/Image';
import useAuthenticated from '../data/auth/useAuthenticated';
import HomeViewerQuery from '../data/queries/HomeViewerQuery';

const VIDEOASK_FORM_ID = 'fq9xkubks';
const VIDEOASK_BASE_URL = 'https://www.videoask.com';

/**
 * VideoAsk embedded page component
 *
 * Embeds a VideoAsk form for user responses with automatic field population
 * from authenticated user data. When the user completes the form, their
 * respondent ID is saved via the VideoAsk webhook handler.
 */
export default function VideoAsk() {
  const navigate = useNavigate();
  const { user } = useAuthenticated(HomeViewerQuery);

  useEffect(() => {
    // Build VideoAsk URL with query parameters
    const params = new URLSearchParams({
      autoplay: 'true',
    });

    // Populate contact fields from user data if available
    if (user?.email) {
      params.append('contact_email', user.email);
    }
    if (user?.firstName) {
      params.append('contact_name', user.firstName);
    }

    // Build the full iframe URL
    const iframeUrl = `${VIDEOASK_BASE_URL}/${VIDEOASK_FORM_ID}?${params.toString()}`;

    // Create and inject the VideoAsk iframe
    const container = document.getElementById('videoask-container');
    if (container) {
      container.innerHTML = '';

      const iframe = document.createElement('iframe');
      iframe.src = iframeUrl;
      iframe.style.width = '100%';
      iframe.style.height = '100vh';
      iframe.style.border = 'none';
      iframe.allow = 'camera; microphone; autoplay; encrypted-media;';
      iframe.setAttribute('allowfullscreen', 'true');

      container.appendChild(iframe);
    }

    // Listen for VideoAsk messages
    const isVideoaskMessage = (message: MessageEvent) =>
      message.origin === 'https://www.videoask.com' &&
      message.data &&
      message.data.type &&
      message.data.type.startsWith('videoask_');

    const handleMessage = (message: MessageEvent) => {
      if (!isVideoaskMessage(message)) {
        return;
      }

      // Handle VideoAsk completion
      if (message.data.type === 'videoask_submitted') {
        // Set localStorage flag to immediately hide the block
        if (user?._id) {
          localStorage.setItem(`videoask_completed_${user._id}`, 'true');
        }

        // Redirect to home page after completion
        setTimeout(() => {
          navigate('/');
        }, 1000);
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [user, navigate]);

  return (
    <Box
      sx={{
        backgroundColor: '#38191e',
        minHeight: '100vh',
      }}
    >
      <Helmet>
        <title>Relate | Berätta vem du är – med dina egna ord</title>
      </Helmet>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          p: 2,
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1,
        }}
      >
        <Image src="/logo.png" sx={{ width: 65 }} />
      </Box>
      <div id="videoask-container" style={{ width: '100%', height: '100vh' }} />
    </Box>
  );
}
